import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { fetchData } from '../../helpers/apiUtils';
import {
  WebinarMailingCampaign,
  MailingType,
  CampaignStatus,
} from '../../helpers/webinarMailingData';
import moment from 'moment';

interface CampaignListProps {
  onEdit: (campaignId: number) => void;
  onNew: () => void;
}

const CampaignList: React.FC<CampaignListProps> = ({ onEdit, onNew }) => {
  const [campaigns, setCampaigns] = useState<WebinarMailingCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<MailingType | ''>('');
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | ''>('');

  const loadCampaigns = async () => {
    setLoading(true);
    setError(null);

    try {
      let url = '/api/webinar-mailings/campaigns?limit=100';
      if (typeFilter) url += `&mailing_type=${typeFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;

      const response = await fetchData<WebinarMailingCampaign[]>(url);
      if (response.success && response.data) {
        setCampaigns(response.data);
      } else {
        setError('Fehler beim Laden der Kampagnen');
      }
    } catch (err) {
      setError('Fehler beim Laden der Kampagnen');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, [typeFilter, statusFilter]);

  const handleDelete = async (campaignId: number) => {
    if (!window.confirm('Kampagne wirklich löschen?')) return;

    try {
      const response = await fetchData(`/api/webinar-mailings/campaigns/${campaignId}`, {
        method: 'DELETE',
      });
      if (response.success) {
        loadCampaigns();
      } else {
        alert('Fehler beim Löschen: ' + (response.error || 'Unbekannter Fehler'));
      }
    } catch (err) {
      alert('Fehler beim Löschen');
      console.error(err);
    }
  };

  const handleDuplicate = async (campaignId: number) => {
    const dateStr = window.prompt('Ziel-Datum für den neuen Termin (YYYY-MM-DD):');
    if (!dateStr) return;

    // Basic format check
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      alert('Ungültiges Datumsformat. Bitte YYYY-MM-DD verwenden.');
      return;
    }

    try {
      const response = await fetchData<{ id: number; emails_copied: number }>(
        `/api/webinar-mailings/campaigns/${campaignId}/duplicate`,
        {
          method: 'POST',
          body: JSON.stringify({ occurrence_date: dateStr }),
        },
      );
      if (response.success && response.data) {
        onEdit(response.data.id);
      } else {
        alert('Fehler: ' + (response.error || 'Unbekannter Fehler'));
      }
    } catch (err) {
      alert('Fehler beim Duplizieren');
      console.error(err);
    }
  };

  const getStatusChip = (status: CampaignStatus) => {
    const colors: Record<CampaignStatus, 'default' | 'primary' | 'success' | 'error'> = {
      draft: 'default',
      scheduled: 'primary',
      completed: 'success',
      cancelled: 'error',
    };
    const labels: Record<CampaignStatus, string> = {
      draft: 'Entwurf',
      scheduled: 'Geplant',
      completed: 'Abgeschlossen',
      cancelled: 'Abgebrochen',
    };
    return <Chip size="small" label={labels[status]} color={colors[status]} />;
  };

  const getTypeChip = (type: MailingType, emailTypes?: string) => {
    // Show "Gemischt" for campaigns with mixed email types
    if (emailTypes && emailTypes.includes(',')) {
      return (
        <Chip size="small" label="Gemischt" sx={{ backgroundColor: '#9c27b0', color: 'white' }} />
      );
    }
    // Use actual email types if available, fallback to campaign-level type
    const actualType = emailTypes || type;
    return (
      <Chip
        size="small"
        label={actualType === 'heatup' ? 'Heat-Up' : 'Redlink'}
        sx={{
          backgroundColor: actualType === 'heatup' ? '#ff9800' : '#2196f3',
          color: 'white',
        }}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Typ</InputLabel>
            <Select
              value={typeFilter}
              label="Typ"
              onChange={(e) => setTypeFilter(e.target.value as MailingType | '')}
            >
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="heatup">Heat-Up</MenuItem>
              <MenuItem value="redlink">Redlink</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as CampaignStatus | '')}
            >
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="draft">Entwurf</MenuItem>
              <MenuItem value="scheduled">Geplant</MenuItem>
              <MenuItem value="completed">Abgeschlossen</MenuItem>
              <MenuItem value="cancelled">Abgebrochen</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={onNew}>
          Neue Kampagne
        </Button>
      </Box>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                ID
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Typ
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Name
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Webinar
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Webinar Datum
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Termin
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                E-Mails
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Erstellt
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#e0e0e0 !important',
                  fontWeight: 'bold',
                }}
              >
                Aktionen
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  Keine Kampagnen gefunden
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((campaign) => (
                <TableRow key={campaign.id} hover>
                  <TableCell>{campaign.id}</TableCell>
                  <TableCell>{getTypeChip(campaign.mailing_type, campaign.email_types)}</TableCell>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {campaign.webinar_title || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {campaign.webinar_date
                      ? moment.utc(campaign.webinar_date).format('DD.MM.YYYY HH:mm')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {campaign.occurrence_date
                      ? moment(campaign.occurrence_date).format('DD.MM.YYYY')
                      : '-'}
                  </TableCell>
                  <TableCell>{campaign.email_count || 0}</TableCell>
                  <TableCell>{getStatusChip(campaign.status)}</TableCell>
                  <TableCell>{moment(campaign.created_at).format('DD.MM.YYYY')}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => onEdit(campaign.id)} title="Bearbeiten">
                      <EditIcon />
                    </IconButton>
                    {Boolean(campaign.webinar_is_recurring) && (
                      <IconButton
                        size="small"
                        onClick={() => handleDuplicate(campaign.id)}
                        title="Für neuen Termin duplizieren"
                        color="info"
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    )}
                    {campaign.status === 'draft' && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(campaign.id)}
                        title="Löschen"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CampaignList;
